import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { Link2, Calendar, Clock, Paperclip, Upload, Download, Trash2, FileText, Image, File, Eye, X, MessageSquare, Send, AlertTriangle, AlertCircle, CheckCircle2, User, CalendarClock, Percent, Info } from 'lucide-react';
import { TimeTrackingWidget } from './TimeTrackingWidget';
import OnlineStatus from './OnlineStatus';
import { Badge } from '@/components/ui/badge';

const TaskDialog = ({ 
  open, 
  onOpenChange, 
  projectId, 
  users, 
  task, 
  tasks = [],
  onSuccess, 
  onCommentsChange,
  onCommentsSeen,
  mode = 'create',
  currentUserId = null,
  defaultTab = 'details'
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to_id: '',
    depends_on_id: '',
    start_date: '',
    due_date: '',
    planned_days: '',
    planned_cost: '',
    priority: 'medium',
    allocation_percent: 100,  // Default: 100% of available project hours
    is_external: false  // External dependency flag
  });

  // Resource availability state
  const [assigneeAvailability, setAssigneeAvailability] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [planningSettings, setPlanningSettings] = useState(null);
  const [assignmentOptions, setAssignmentOptions] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && task) {
      setFormData({
        title: task.title,
        description: task.description,
        assigned_to_id: task.assigned_to_id,
        depends_on_id: task.depends_on && task.depends_on.length > 0 ? task.depends_on[0] : '',
        start_date: task.start_date || '',
        due_date: task.due_date,
        planned_days: '',
        planned_cost: task.planned_cost.toString(),
        priority: task.priority || 'medium',
        allocation_percent: task.allocation_percent || 100,
        is_external: task.is_external || false
      });
      // Reset base duration when editing existing task
      setBaseDuration(null);
    } else if (mode === 'create' && open) {
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        title: '',
        description: '',
        assigned_to_id: currentUserId || '',
        depends_on_id: '',
        start_date: today,
        due_date: today,
        planned_days: '',
        planned_cost: '',
        priority: 'medium',
        allocation_percent: 100,
        is_external: false
      });
      // Reset base duration for new task
      setBaseDuration(null);
    }
  }, [mode, task, open, currentUserId]);

  // Fetch planning settings when dialog opens
  useEffect(() => {
    if (open) {
      fetchPlanningSettings();
    }
  }, [open]);

  const fetchPlanningSettings = async () => {
    try {
      const response = await axios.get(`${API}/resource-planning/planning-settings`);
      setPlanningSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch planning settings', error);
    }
  };

  // Sync activeTab with defaultTab when dialog opens
  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab);
    }
  }, [open, defaultTab]);

  // Mark comments as seen when comments tab is active
  useEffect(() => {
    if (activeTab === 'comments' && task?.id && onCommentsSeen) {
      onCommentsSeen(task.id);
    }
  }, [activeTab, task?.id, onCommentsSeen]);

  // Check assignee availability when assigned person or dates change (skip for external tasks)
  useEffect(() => {
    const checkAvailability = async () => {
      // Skip availability check for external tasks
      if (formData.is_external) {
        setAssigneeAvailability(null);
        setAssignmentOptions(null);
        return;
      }
      
      if (!formData.assigned_to_id || !formData.start_date || !formData.due_date) {
        setAssigneeAvailability(null);
        setAssignmentOptions(null);
        return;
      }
      
      setCheckingAvailability(true);
      try {
        const response = await axios.get(
          `${API}/resource-planning/user-availability/${formData.assigned_to_id}`,
          {
            params: {
              start_date: formData.start_date,
              end_date: formData.due_date
            }
          }
        );
        setAssigneeAvailability(response.data);
        
        // Fetch additional options if overbooked OR has high utilization (>=80%)
        // This allows users to see alternative options even when not technically overbooked
        const shouldShowOptions = response.data.is_overbooked || 
          response.data.status === 'high' || 
          (response.data.utilization_percent && response.data.utilization_percent >= 80);
        
        if (shouldShowOptions) {
          setLoadingOptions(true);
          try {
            const optionsResponse = await axios.post(
              `${API}/resource-planning/check-assignment`,
              null,
              {
                params: {
                  user_id: formData.assigned_to_id,
                  start_date: formData.start_date,
                  end_date: formData.due_date,
                  allocation_percent: formData.allocation_percent || 100
                }
              }
            );
            setAssignmentOptions(optionsResponse.data);
          } catch (optErr) {
            console.log('Assignment options not available:', optErr);
            setAssignmentOptions(null);
          } finally {
            setLoadingOptions(false);
          }
        } else {
          setAssignmentOptions(null);
        }
      } catch (error) {
        // If resource planning module is not enabled or other error, just ignore
        console.log('Availability check not available:', error.response?.status);
        setAssigneeAvailability(null);
        setAssignmentOptions(null);
      } finally {
        setCheckingAvailability(false);
      }
    };
    
    // Debounce the check
    const timer = setTimeout(checkAvailability, 300);
    return () => clearTimeout(timer);
  }, [formData.assigned_to_id, formData.start_date, formData.due_date, formData.allocation_percent]);

  const handleDependencyChange = (newDependencyId) => {
    setFormData(prev => {
      const newData = { ...prev, depends_on_id: newDependencyId };
      
      if (newDependencyId && newDependencyId !== 'none') {
        const depTask = tasks.find(t => t.id === newDependencyId);
        if (depTask && depTask.due_date) {
          const depEndDate = new Date(depTask.due_date);
          const newStartDate = new Date(depEndDate);
          newStartDate.setDate(newStartDate.getDate() + 1);
          newData.start_date = newStartDate.toISOString().split('T')[0];
          
          if (prev.planned_days) {
            const dueDate = new Date(newStartDate);
            dueDate.setDate(dueDate.getDate() + parseInt(prev.planned_days) - 1);
            newData.due_date = dueDate.toISOString().split('T')[0];
          }
        }
      }
      
      return newData;
    });
  };

  const handlePlannedDaysChange = (days) => {
    setFormData(prev => {
      const newData = { ...prev, planned_days: days };
      
      if (days && prev.start_date) {
        const startDate = new Date(prev.start_date);
        const dueDate = new Date(startDate);
        dueDate.setDate(dueDate.getDate() + parseInt(days) - 1);
        newData.due_date = dueDate.toISOString().split('T')[0];
      }
      
      return newData;
    });
  };

  // Track base duration (at 100% allocation) for proportional calculation
  const [baseDuration, setBaseDuration] = useState(null);

  const handleAllocationChange = (newAllocation) => {
    setFormData(prev => {
      const newData = { ...prev, allocation_percent: newAllocation };
      
      // Use baseDuration from state (captured at render time)
      // If we have a base duration and start date, recalculate the actual duration
      const currentBaseDuration = baseDuration;
      if (currentBaseDuration && prev.start_date) {
        // new_duration = base_duration * (100 / new_allocation)
        const newDays = Math.ceil(currentBaseDuration * (100 / newAllocation));
        newData.planned_days = newDays.toString();
        
        // Update due date based on new duration
        const startDate = new Date(prev.start_date);
        const dueDate = new Date(startDate);
        dueDate.setDate(dueDate.getDate() + newDays - 1);
        newData.due_date = dueDate.toISOString().split('T')[0];
      }
      
      return newData;
    });
  };

  // When duration is manually entered, save it as base duration (at current allocation)
  const handleDurationWithBaseUpdate = (days) => {
    if (days && days !== '') {
      const daysNum = parseFloat(days);
      if (daysNum > 0) {
        // Calculate base duration at 100% allocation
        const currentAllocation = formData.allocation_percent || 100;
        // If entering at 100%, base = days. If entering at 50%, base = days * 0.5 (half the work)
        const baseAtFullAllocation = Math.round(daysNum * (currentAllocation / 100));
        setBaseDuration(baseAtFullAllocation > 0 ? baseAtFullAllocation : daysNum);
        console.log(`Setting baseDuration: ${daysNum} days at ${currentAllocation}% = ${baseAtFullAllocation > 0 ? baseAtFullAllocation : daysNum} base days`);
      }
    } else {
      setBaseDuration(null);
    }
    handlePlannedDaysChange(days);
  };

  const handleStartDateChange = (newStartDate) => {
    setFormData(prev => {
      const newData = { ...prev, start_date: newStartDate };
      
      if (prev.planned_days && newStartDate) {
        const startDate = new Date(newStartDate);
        const dueDate = new Date(startDate);
        dueDate.setDate(dueDate.getDate() + parseInt(prev.planned_days) - 1);
        newData.due_date = dueDate.toISOString().split('T')[0];
      }
      
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const taskData = {
        title: formData.title,
        description: formData.description,
        assigned_to_id: formData.assigned_to_id,
        start_date: formData.start_date || null,
        due_date: formData.due_date,
        planned_cost: parseFloat(formData.planned_cost),
        priority: formData.priority,
        allocation_percent: formData.allocation_percent || 100,  // Daily allocation percentage
        is_external: formData.is_external || false,  // External dependency flag
        depends_on: formData.depends_on_id && formData.depends_on_id !== 'none' 
          ? [formData.depends_on_id] 
          : []
      };

      if (mode === 'create') {
        taskData.project_id = projectId;
        await axios.post(`${API}/tasks`, taskData);
        toast.success('Task erfolgreich erstellt!');
      } else {
        await axios.put(`${API}/tasks/${task.id}`, taskData);
        toast.success('Task erfolgreich aktualisiert!');
      }

      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Fehler beim ${mode === 'create' ? 'Erstellen' : 'Aktualisieren'} des Tasks`);
    }
  };

  // Attachment state and functions
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Comment state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const commentsEndRef = useRef(null);
  
  // @mention state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionCursorPosition, setMentionCursorPosition] = useState(0);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const commentInputRef = useRef(null);

  useEffect(() => {
    if (mode === 'edit' && task?.id && open) {
      fetchAttachments();
      fetchComments();
    }
  }, [mode, task?.id, open]);

  const fetchAttachments = async () => {
    if (!task?.id) return;
    try {
      const response = await axios.get(`${API}/tasks/${task.id}/attachments`);
      setAttachments(response.data);
    } catch (error) {
      console.error('Failed to fetch attachments', error);
    }
  };

  const fetchComments = async () => {
    if (!task?.id) return;
    try {
      const response = await axios.get(`${API}/tasks/${task.id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments', error);
    }
  };

  const handleSubmitComment = async (e) => {
    e?.preventDefault();
    if (!newComment.trim() || !task?.id) return;

    setSubmittingComment(true);
    try {
      await axios.post(`${API}/tasks/${task.id}/comments`, { content: newComment.trim() });
      setNewComment('');
      fetchComments();
      toast.success('Kommentar hinzugefügt');
      // Notify parent about comment change
      onCommentsChange?.(task.id);
      // Scroll to bottom
      setTimeout(() => {
        commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      toast.error('Fehler beim Hinzufügen des Kommentars');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Möchten Sie diesen Kommentar wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/comments/${commentId}`);
      toast.success('Kommentar gelöscht');
      fetchComments();
      // Notify parent about comment change
      onCommentsChange?.(task?.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  // Handle @mention input
  const handleCommentChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setNewComment(value);
    
    // Check if we're typing after an @ symbol
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Check if there's no space after @ (still typing the mention)
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        setMentionFilter(textAfterAt.toLowerCase());
        setMentionCursorPosition(lastAtIndex);
        setShowMentionDropdown(true);
        setSelectedMentionIndex(0); // Reset selection when filter changes
        return;
      }
    }
    setShowMentionDropdown(false);
  };

  // Get filtered users for mention dropdown
  const filteredMentionUsers = users.filter(u => 
    u.name.toLowerCase().includes(mentionFilter) && u.id !== currentUserId
  );

  // Handle selecting a user from mention dropdown
  const handleSelectMention = (user) => {
    const beforeMention = newComment.substring(0, mentionCursorPosition);
    const afterCursor = newComment.substring(mentionCursorPosition + 1 + mentionFilter.length);
    const newValue = `${beforeMention}@${user.name} ${afterCursor}`;
    setNewComment(newValue);
    setShowMentionDropdown(false);
    setMentionFilter('');
    setSelectedMentionIndex(0);
    
    // Set cursor position after the inserted mention + space
    const newCursorPosition = mentionCursorPosition + user.name.length + 2; // +2 for @ and space
    setTimeout(() => {
      if (commentInputRef.current) {
        commentInputRef.current.focus();
        commentInputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 10);
  };

  // Close mention dropdown on escape or click outside
  const handleCommentKeyDown = (e) => {
    if (showMentionDropdown && filteredMentionUsers.length > 0) {
      if (e.key === 'Escape') {
        setShowMentionDropdown(false);
        e.preventDefault();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev < filteredMentionUsers.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev > 0 ? prev - 1 : filteredMentionUsers.length - 1
        );
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        handleSelectMention(filteredMentionUsers[selectedMentionIndex]);
      }
    }
  };

  const formatCommentDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffHours < 24) return `vor ${diffHours} Std.`;
    if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
    return date.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !task?.id) return;

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Datei zu gross. Maximum: 10 MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/tasks/${task.id}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Datei erfolgreich hochgeladen');
      fetchAttachments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownload = async (attachment) => {
    try {
      const response = await axios.get(`${API}/attachments/${attachment.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Fehler beim Herunterladen');
    }
  };

  // Preview state and functions
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFilename, setPreviewFilename] = useState('');

  const isPreviewable = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'pdf'].includes(ext);
  };

  const isImage = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
  };

  const handlePreview = async (attachment) => {
    const ext = attachment.original_filename.split('.').pop()?.toLowerCase();
    
    try {
      const response = await axios.get(`${API}/attachments/${attachment.id}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: attachment.content_type });
      const url = window.URL.createObjectURL(blob);
      
      if (ext === 'pdf') {
        window.open(url, '_blank');
      } else if (isImage(attachment.original_filename)) {
        setPreviewFilename(attachment.original_filename);
        setPreviewUrl(url);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Vorschau');
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewFilename('');
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Möchten Sie diesen Anhang wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/attachments/${attachmentId}`);
      toast.success('Anhang gelöscht');
      fetchAttachments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
      return <Image className="w-5 h-5 text-green-500" />;
    }
    if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) {
      return <FileText className="w-5 h-5 text-blue-500" />;
    }
    return <File className="w-5 h-5 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const availableDependencies = tasks.filter(t => {
    if (mode === 'edit' && task) {
      if (t.id === task.id) return false;
      if (t.depends_on && t.depends_on.includes(task.id)) return false;
    }
    return true;
  });

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="task-title">Task Titel</Label>
        <Input
          id="task-title"
          data-testid="task-title-input"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="z.B. Design erstellen"
          required
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="task-description">Beschreibung</Label>
        <Textarea
          id="task-description"
          data-testid="task-description-input"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Beschreiben Sie den Task..."
          rows={2}
          required
        />
      </div>

      {/* Assigned To */}
      <div className="space-y-2">
        <Label htmlFor="task-assigned">Zuständig</Label>
        <Select
          value={formData.assigned_to_id}
          onValueChange={(value) => setFormData({ ...formData, assigned_to_id: value })}
        >
          <SelectTrigger data-testid="task-assigned-select">
            <SelectValue placeholder="Mitarbeiter auswählen" />
          </SelectTrigger>
          <SelectContent>
            {users.map((user) => (
              <SelectItem key={user.id} value={user.id}>
                {user.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Availability Warning */}
        {assigneeAvailability && assigneeAvailability.warning && (
          <div 
            className={`flex flex-col gap-2 p-2 rounded-md text-xs ${
              assigneeAvailability.status === 'overbooked' 
                ? 'bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300' 
                : assigneeAvailability.status === 'high'
                  ? 'bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300'
                  : 'bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300'
            }`}
            data-testid="assignee-availability-warning"
          >
            <div className="flex items-start gap-2">
              {assigneeAvailability.status === 'overbooked' ? (
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              ) : assigneeAvailability.status === 'high' ? (
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              ) : (
                <Calendar className="w-4 h-4 mt-0.5 shrink-0" />
              )}
              <div>
                <p className="font-medium">{assigneeAvailability.warning}</p>
                {assigneeAvailability.project_allocations?.length > 0 && (
                  <p className="mt-0.5 opacity-80">
                    Projekte: {assigneeAvailability.project_allocations.slice(0, 3).join(', ')}
                    {assigneeAvailability.project_allocations.length > 3 && ' ...'}
                  </p>
                )}
                {assigneeAvailability.has_absence && (
                  <p className="mt-0.5 opacity-80">
                    {assigneeAvailability.absence_count} Abwesenheit(en) im Zeitraum
                  </p>
                )}
              </div>
            </div>
            
            {/* Assignment Options when overbooked OR high utilization */}
            {(assigneeAvailability.is_overbooked || assigneeAvailability.status === 'high' || assigneeAvailability.utilization_percent >= 80) && assignmentOptions && (
              <div className={`mt-2 pt-2 border-t space-y-2 ${assigneeAvailability.is_overbooked ? 'border-red-300 dark:border-red-700' : 'border-yellow-300 dark:border-yellow-700'}`}>
                <p className={`font-semibold ${assigneeAvailability.is_overbooked ? 'text-red-800 dark:text-red-200' : 'text-yellow-800 dark:text-yellow-200'}`}>
                  {assigneeAvailability.is_overbooked ? 'Optionen:' : 'Planungs-Optionen:'}
                </p>
                
                {/* Option 1: Earliest available date */}
                {assignmentOptions.earliest_available_date && (
                  <div className="flex items-center justify-between bg-card/80 dark:bg-slate-800/80 rounded p-1.5 border border-border">
                    <div className="flex items-center gap-1.5">
                      <CalendarClock className="w-3.5 h-3.5" />
                      <span>Frühestes Datum: {new Date(assignmentOptions.earliest_available_date).toLocaleDateString('de-CH')}</span>
                    </div>
                    <Button 
                      type="button"
                      variant="outline" 
                      size="sm" 
                      className="h-6 text-xs px-2"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          start_date: assignmentOptions.earliest_available_date,
                          due_date: assignmentOptions.earliest_available_end
                        }));
                      }}
                    >
                      Übernehmen
                    </Button>
                  </div>
                )}
                
                {/* Option 2: Alternative users */}
                {assignmentOptions.alternative_users?.length > 0 && (
                  <div className="bg-card/80 dark:bg-slate-800/80 rounded p-1.5 border border-border">
                    <div className="flex items-center gap-1.5 mb-1">
                      <User className="w-3.5 h-3.5" />
                      <span className="font-medium">Alternative Personen:</span>
                    </div>
                    <div className="space-y-1 ml-5">
                      {assignmentOptions.alternative_users.slice(0, 3).map(alt => (
                        <div key={alt.user_id} className="flex items-center justify-between">
                          <span>{alt.user_name} ({alt.utilization_percent}% ausgelastet)</span>
                          <Button 
                            type="button"
                            variant="outline" 
                            size="sm" 
                            className="h-5 text-xs px-2"
                            onClick={() => setFormData(prev => ({ ...prev, assigned_to_id: alt.user_id }))}
                          >
                            Zuweisen
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Option 3: Reduced allocation - only show feasible options */}
                {assignmentOptions.allocation_options?.length > 0 && (
                  <div className="bg-card/80 dark:bg-slate-800/80 rounded p-1.5 border border-border">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Percent className="w-3.5 h-3.5" />
                      <span className="font-medium">Allokation reduzieren:</span>
                    </div>
                    <div className="space-y-1 ml-5">
                      {/* Show smart allocation suggestions if available */}
                      {assignmentOptions.suggested_allocations?.length > 0 && (
                        <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-950/50 rounded border border-blue-200 dark:border-blue-700">
                          <div className="flex items-center gap-1 mb-1">
                            <p className="text-xs font-medium text-blue-700 dark:text-blue-300">Empfohlen:</p>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="w-3 h-3 text-blue-500 dark:text-blue-400 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs">
                                  <p>Nur Allokation ändern, Enddatum bleibt gleich. Die geplante Arbeitsmenge wird reduziert.</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          {assignmentOptions.suggested_allocations.map(sug => (
                            <div key={sug.type} className="flex items-center justify-between text-xs">
                              <span className="text-blue-600 dark:text-blue-400">{sug.allocation_percent}% - {sug.reason}</span>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      type="button"
                                      variant="outline" 
                                      size="sm" 
                                      className="h-5 text-xs px-2 border-blue-300 dark:border-blue-600"
                                      onClick={() => setFormData(prev => ({ ...prev, allocation_percent: sug.allocation_percent }))}
                                    >
                                      Wählen
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="max-w-xs">
                                    <p>Setzt Allokation auf {sug.allocation_percent}%. Enddatum bleibt unverändert – weniger Arbeit wird geleistet.</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Show max overbooking limit info */}
                      {assignmentOptions.max_overbooking_percent && (
                        <div className="flex items-center gap-1 mb-1">
                          <p className="text-xs text-muted-foreground">
                            Max. erlaubte Auslastung: {assignmentOptions.max_overbooking_percent}%
                          </p>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs">
                                <p>Allokation + Enddatum werden angepasst. Die geplante Arbeitsmenge bleibt gleich, verteilt sich aber über mehr Tage.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      )}
                      
                      {/* Only show feasible allocation options */}
                      {assignmentOptions.allocation_options.filter(opt => opt.is_feasible).map(opt => (
                        <div key={opt.allocation_percent} className="flex items-center justify-between p-1.5 rounded bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                          <span className="text-green-800 dark:text-green-300">
                            {opt.allocation_percent}% → bis {new Date(opt.new_end_date).toLocaleDateString('de-CH')} 
                            <span className="text-green-600 dark:text-green-400 ml-1">(+{opt.extension_days} Tage)</span>
                          </span>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  type="button"
                                  variant="outline" 
                                  size="sm" 
                                  className="h-5 text-xs px-2 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300"
                                  onClick={() => setFormData(prev => ({ 
                                    ...prev, 
                                    allocation_percent: opt.allocation_percent,
                                    due_date: opt.new_end_date
                                  }))}
                                >
                                  Anpassen
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="max-w-xs">
                                <p>Setzt Allokation auf {opt.allocation_percent}% und verlängert bis {new Date(opt.new_end_date).toLocaleDateString('de-CH')} – gleiche Arbeitsmenge, mehr Zeit.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      ))}
                      
                      {/* Show hint if no feasible options and all exceed limit */}
                      {assignmentOptions.allocation_options.filter(opt => opt.is_feasible).length === 0 && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 italic">
                          Keine Allokation möglich ohne {assignmentOptions.max_overbooking_percent}% zu überschreiten.
                          Nutzen Sie "Alternative Personen" oder "Frühestes Datum".
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                {loadingOptions && (
                  <p className="text-center text-red-600 opacity-70">Lade Optionen...</p>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Availability OK Badge */}
        {assigneeAvailability && !assigneeAvailability.warning && assigneeAvailability.status === 'available' && (
          <div className="flex items-center gap-1.5 text-xs text-green-600">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>Verfügbar ({assigneeAvailability.available_hours}h frei)</span>
          </div>
        )}
      </div>

      {/* Priority */}
      <div className="space-y-2">
        <Label htmlFor="task-priority">Priorität</Label>
        <Select
          value={formData.priority}
          onValueChange={(value) => setFormData({ ...formData, priority: value })}
        >
          <SelectTrigger data-testid="task-priority-select">
            <SelectValue placeholder="Priorität wählen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="high">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                Hoch
              </div>
            </SelectItem>
            <SelectItem value="medium">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                Mittel
              </div>
            </SelectItem>
            <SelectItem value="low">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                Niedrig
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* External Dependency Checkbox */}
      <div className="flex items-start gap-3 p-3 border rounded-lg bg-slate-50/50 dark:bg-slate-900/30">
        <input
          type="checkbox"
          id="task-is-external"
          data-testid="task-is-external-checkbox"
          checked={formData.is_external}
          onChange={(e) => setFormData({ ...formData, is_external: e.target.checked })}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
        />
        <div className="flex-1">
          <Label htmlFor="task-is-external" className="cursor-pointer font-medium">
            Externe Abhängigkeit
          </Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Dieser Task blockiert keine Ressourcen und wird in der Kapazitätsplanung nicht berücksichtigt.
            Ideal für Lieferanten-Tasks, externe Freigaben oder Wartezeiten.
          </p>
        </div>
      </div>

      {/* Dependency and Duration */}
      <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Link2 className="w-4 h-4" />
          Abhängigkeit & Planung
        </div>
        
        {/* Dependency */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="task-dependency" className="text-xs">Abhängig von</Label>
            <Select
              value={formData.depends_on_id}
              onValueChange={handleDependencyChange}
            >
              <SelectTrigger data-testid="task-dependency-select" className="text-xs">
                <SelectValue placeholder="Keine" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Keine Abhängigkeit</SelectItem>
                {availableDependencies.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="task-duration" className="text-xs">
              <Clock className="w-3 h-3 inline mr-1" />
              Dauer (Tage)
            </Label>
            <Input
              id="task-duration"
              type="number"
              min="1"
              data-testid="task-duration-input"
              value={formData.planned_days}
              onChange={(e) => handleDurationWithBaseUpdate(e.target.value)}
              placeholder="z.B. 5"
              className="text-xs"
            />
            {formData.depends_on_id && formData.depends_on_id !== 'none' && (
              <div className="text-[10px] text-muted-foreground">
                Start automatisch nach Vorgänger
              </div>
            )}
          </div>
        </div>

        {/* Allocation Percent */}
        <div className="space-y-2 p-3 rounded-lg border bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between">
            <Label htmlFor="task-allocation" className="text-xs font-medium">
              Tägliche Allokation
            </Label>
            <Select
              value={formData.allocation_percent?.toString() || "100"}
              onValueChange={(value) => handleAllocationChange(parseInt(value))}
            >
              <SelectTrigger className="w-24 h-8 text-sm" data-testid="task-allocation-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="100">100%</SelectItem>
                <SelectItem value="90">90%</SelectItem>
                <SelectItem value="80">80%</SelectItem>
                <SelectItem value="70">70%</SelectItem>
                <SelectItem value="60">60%</SelectItem>
                <SelectItem value="50">50%</SelectItem>
                <SelectItem value="40">40%</SelectItem>
                <SelectItem value="30">30%</SelectItem>
                <SelectItem value="20">20%</SelectItem>
                <SelectItem value="10">10%</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {planningSettings && (
            <div className="text-[10px] text-blue-600 dark:text-blue-400 mt-1">
              {formData.allocation_percent}% von {planningSettings.available_project_hours}h = {(planningSettings.available_project_hours * formData.allocation_percent / 100).toFixed(1)}h/Tag für diesen Task
            </div>
          )}
          {baseDuration && formData.allocation_percent < 100 && (
            <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-1 p-2 bg-amber-50 dark:bg-amber-950/30 rounded border border-amber-200 dark:border-amber-800">
              📊 Basis: {baseDuration} Tage bei 100% → Bei {formData.allocation_percent}%: {Math.ceil(baseDuration * (100 / formData.allocation_percent))} Tage
            </div>
          )}
        </div>

        {/* Start and End Date */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="task-start-date">Startdatum</Label>
            <Input
              id="task-start-date"
              type="date"
              data-testid="task-start-date-input"
              value={formData.start_date}
              onChange={(e) => handleStartDateChange(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="task-due-date">Enddatum</Label>
            <Input
              id="task-due-date"
              type="date"
              data-testid="task-due-date-input"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              required
            />
          </div>
        </div>
        
        {formData.start_date && formData.due_date && (
          <div className="text-xs text-muted-foreground text-right">
            Dauer: {Math.ceil((new Date(formData.due_date) - new Date(formData.start_date)) / (1000 * 60 * 60 * 24)) + 1} Tage
          </div>
        )}
      </div>

      {/* Planned Cost */}
      <div className="space-y-2">
        <Label htmlFor="task-planned-cost">Geplante Kosten (CHF)</Label>
        <Input
          id="task-planned-cost"
          type="number"
          step="0.01"
          min="0"
          data-testid="task-planned-cost-input"
          value={formData.planned_cost}
          onChange={(e) => setFormData({ ...formData, planned_cost: e.target.value })}
          placeholder="z.B. 5000"
          required
        />
      </div>

      <Button type="submit" className="w-full" data-testid="task-submit-button">
        {mode === 'create' ? 'Task erstellen' : 'Task aktualisieren'}
      </Button>
    </form>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={mode === 'create' ? 'create-task-dialog' : 'edit-task-dialog'} className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Neuen Task erstellen' : 'Task bearbeiten'}</DialogTitle>
          <DialogDescription>
            {mode === 'create' ? 'Fügen Sie einen neuen Task hinzu' : 'Ändern Sie die Task-Details'}
          </DialogDescription>
        </DialogHeader>
        
        {mode === 'edit' && task ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="time">
                <Clock className="w-4 h-4 mr-1" />
                Zeit
              </TabsTrigger>
              <TabsTrigger value="comments">
                <MessageSquare className="w-4 h-4 mr-1" />
                {comments.length > 0 ? `(${comments.length})` : ''}
              </TabsTrigger>
              <TabsTrigger value="attachments">
                <Paperclip className="w-4 h-4 mr-1" />
                {attachments.length > 0 ? `(${attachments.length})` : ''}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="details" className="mt-4">
              {renderForm()}
            </TabsContent>
            
            <TabsContent value="time" className="mt-4">
              <TimeTrackingWidget 
                taskId={task.id} 
                taskTitle={task.title}
                projectId={projectId}
                onTimeUpdated={onSuccess}
              />
            </TabsContent>

            <TabsContent value="comments" className="mt-4">
              <div className="space-y-4">
                {/* Comments List */}
                <div className="max-h-[300px] overflow-y-auto space-y-3 pr-1">
                  {comments.length === 0 ? (
                    <div className="text-center text-muted-foreground py-8">
                      <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>Noch keine Kommentare</p>
                      <p className="text-sm">Schreiben Sie den ersten Kommentar!</p>
                    </div>
                  ) : (
                    comments.map((comment) => (
                      <div key={comment.id} className="p-3 bg-muted/50 rounded-lg group">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <span className="text-sm font-semibold text-primary">
                                {comment.user_name?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <div className="font-medium text-sm truncate">{comment.user_name}</div>
                              <div className="text-xs text-muted-foreground">{formatCommentDate(comment.created_at)}</div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            className="text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                            title="Löschen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        {/* Format comment content with bold @mentions */}
                        <p className="mt-2 text-sm whitespace-pre-wrap break-words">
                          {comment.content.split(/(@\w+(?:\s+\w+)?)/g).map((part, i) => 
                            part.startsWith('@') ? (
                              <span key={i} className="font-bold text-primary">{part}</span>
                            ) : part
                          )}
                        </p>
                      </div>
                    ))
                  )}
                  <div ref={commentsEndRef} />
                </div>

                {/* New Comment Input */}
                <form onSubmit={handleSubmitComment} className="relative">
                  {/* @mention dropdown */}
                  {showMentionDropdown && filteredMentionUsers.length > 0 && (
                    <div className="absolute bottom-full left-0 right-0 mb-1 bg-popover border border-border rounded-md shadow-lg z-50 max-h-48 overflow-y-auto">
                      <div className="p-1">
                        <div className="text-xs text-muted-foreground px-2 py-1.5 border-b mb-1">
                          Benutzer erwähnen (↑↓ zum Navigieren, Enter zum Auswählen)
                        </div>
                        {filteredMentionUsers.map((user, index) => (
                          <button
                            key={user.id}
                            type="button"
                            className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md text-left transition-colors ${
                              index === selectedMentionIndex
                                ? 'bg-primary text-primary-foreground'
                                : 'hover:bg-accent'
                            }`}
                            onClick={() => handleSelectMention(user)}
                            onMouseEnter={() => setSelectedMentionIndex(index)}
                          >
                            <div className="relative">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                                index === selectedMentionIndex
                                  ? 'bg-primary-foreground/20'
                                  : 'bg-primary/10'
                              }`}>
                                <span className={`text-sm font-semibold ${
                                  index === selectedMentionIndex
                                    ? 'text-primary-foreground'
                                    : 'text-primary'
                                }`}>
                                  {user.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              {/* Online status indicator */}
                              <OnlineStatus 
                                isOnline={user.is_online} 
                                size="sm"
                                className="absolute -bottom-0.5 -right-0.5 border-2 border-background"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`font-medium truncate flex items-center gap-2 ${
                                index === selectedMentionIndex ? 'text-primary-foreground' : ''
                              }`}>
                                {user.name}
                                {user.is_online && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                    index === selectedMentionIndex 
                                      ? 'bg-green-400/30 text-green-100' 
                                      : 'bg-green-500/10 text-green-600'
                                  }`}>
                                    online
                                  </span>
                                )}
                              </div>
                              {user.email && (
                                <div className={`text-xs truncate ${
                                  index === selectedMentionIndex
                                    ? 'text-primary-foreground/70'
                                    : 'text-muted-foreground'
                                }`}>
                                  {user.email}
                                </div>
                              )}
                            </div>
                            {index === selectedMentionIndex && (
                              <div className="text-xs opacity-70">
                                Enter ↵
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      ref={commentInputRef}
                      value={newComment}
                      onChange={handleCommentChange}
                      onKeyDown={handleCommentKeyDown}
                      placeholder="Kommentar schreiben... (@Name für Erwähnung)"
                      className="flex-1"
                      disabled={submittingComment}
                      data-testid="comment-input"
                    />
                    <Button 
                      type="submit" 
                      size="icon"
                      disabled={!newComment.trim() || submittingComment}
                      data-testid="submit-comment-button"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Tipp: Tippe @ um jemanden zu erwähnen
                  </div>
                </form>
              </div>
            </TabsContent>
            
            <TabsContent value="attachments" className="mt-4">
              <div className="space-y-4">
                {/* Image Preview */}
                {previewUrl && (
                  <div className="relative bg-muted rounded-lg p-4">
                    <button
                      onClick={closePreview}
                      className="absolute top-2 right-2 p-1 bg-background rounded-full shadow hover:bg-muted"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="text-sm font-medium mb-2 truncate pr-8">{previewFilename}</div>
                    <img
                      src={previewUrl}
                      alt={previewFilename}
                      className="max-w-full max-h-[300px] object-contain mx-auto rounded"
                    />
                  </div>
                )}

                {/* Upload Section */}
                <div className="border-2 border-dashed border-muted rounded-lg p-6 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg,.gif,.zip,.csv"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <Upload className={`w-8 h-8 ${uploading ? 'text-muted-foreground animate-pulse' : 'text-primary'}`} />
                    <span className="text-sm font-medium">
                      {uploading ? 'Wird hochgeladen...' : 'Datei auswählen'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      PDF, Word, Excel, Bilder, ZIP (max. 10 MB)
                    </span>
                  </label>
                </div>

                {/* Attachments List */}
                {attachments.length === 0 ? (
                  <div className="text-center text-muted-foreground py-4">
                    Keine Anhänge vorhanden
                  </div>
                ) : (
                  <div className="space-y-2">
                    {attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border hover:bg-muted/70 transition-colors"
                      >
                        <div 
                          className={`flex items-center gap-3 min-w-0 flex-1 ${isPreviewable(attachment.original_filename) ? 'cursor-pointer' : ''}`}
                          onClick={() => isPreviewable(attachment.original_filename) && handlePreview(attachment)}
                        >
                          {getFileIcon(attachment.original_filename)}
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm truncate">
                              {attachment.original_filename}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {formatFileSize(attachment.file_size)} • {attachment.uploaded_by_name}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          {isPreviewable(attachment.original_filename) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handlePreview(attachment)}
                              title="Vorschau"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownload(attachment)}
                            title="Herunterladen"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteAttachment(attachment.id)}
                            title="Löschen"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          renderForm()
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TaskDialog;
